import { TypedDocumentNode } from '@apollo/client';
import gql from 'graphql-tag.macro';
import { defineMessages } from 'react-intl';

import { Position } from '@sorare/core/src/__generated__/globalTypes';
import { withFragments } from '@sorare/core/src/lib/gql';

import { getRemainingBudgetForCurrentPosition_footballRivalsDraftableObjectInterface } from './__generated__/lib.graphql';

export const DraftPosition = {
  Goalkeeper: Position.Goalkeeper,
  Defender: Position.Defender,
  Midfielder: Position.Midfielder,
  Forward: Position.Forward,
  Extra: 'Extra Player',
} as const;

export type DraftPositionType =
  (typeof DraftPosition)[keyof typeof DraftPosition];

export const positions: DraftPositionType[] = [
  DraftPosition.Goalkeeper,
  DraftPosition.Defender,
  DraftPosition.Midfielder,
  DraftPosition.Forward,
  DraftPosition.Extra,
];

export const positionNames = defineMessages<DraftPositionType>({
  [DraftPosition.Goalkeeper]: {
    id: 'Player.goalkeeper',
    defaultMessage: 'Goalkeeper',
  },
  [DraftPosition.Defender]: {
    id: 'Player.defender',
    defaultMessage: 'Defender',
  },
  [DraftPosition.Midfielder]: {
    id: 'Player.midfielder',
    defaultMessage: 'Midfielder',
  },
  [DraftPosition.Forward]: {
    id: 'Player.forward',
    defaultMessage: 'Forward',
  },
  [DraftPosition.Extra]: {
    id: 'Player.shortExtraPlayer',
    defaultMessage: 'Extra',
  },
});

export const findNextEmptyPosition = (
  selectedPosition: DraftPositionType,
  selectedPlayers: Record<DraftPositionType, Record<string, any> | null>
) => {
  const currentPositionIndex = positions.indexOf(selectedPosition);
  const nextPositions = positions.slice(currentPositionIndex + 1);

  const nextEmptyPosition = nextPositions.find(
    position => !selectedPlayers[position]
  );

  if (nextEmptyPosition) {
    return nextEmptyPosition;
  }

  const emptyPosition = positions.find(position => !selectedPlayers[position]);

  if (emptyPosition) {
    return emptyPosition;
  }

  return selectedPosition;
};

export const getRemainingBudgetForCurrentPosition = withFragments(
  ({
    selectedPlayers,
    cap,
    selectedPosition,
  }: {
    selectedPlayers: Record<
      DraftPositionType,
      getRemainingBudgetForCurrentPosition_footballRivalsDraftableObjectInterface | null
    >;
    cap: number;
    selectedPosition: DraftPositionType;
  }) => {
    return (
      cap -
      Object.entries(selectedPlayers).reduce((acc, [position, player]) => {
        if (position === selectedPosition) {
          return acc;
        }
        return acc + (player?.capValue || 0);
      }, 0)
    );
  },
  {
    footballRivalsDraftableObjectInterface: gql`
      fragment getRemainingBudgetForCurrentPosition_footballRivalsDraftableObjectInterface on FootballRivalsDraftableObjectInterface {
        id
        capValue
      }
    ` as TypedDocumentNode<getRemainingBudgetForCurrentPosition_footballRivalsDraftableObjectInterface>,
  }
);
